import { useEffect, useState } from 'react';

/**
 * §10.9 — the beat after a close.
 *
 * A close ends with a question, and then the rep stops talking. Reps do not
 * stop talking, because four seconds of silence on a live call feels like a
 * minute and the reflex is to fill it — usually by softening the thing they
 * just asked for, which is how a close becomes a conversation again.
 *
 * That is not something a lesson conveys. It has to be sat through, so this
 * screen sits through it: the prospect's reply is held back for a real
 * interval, and there is nothing to press. An escape hatch would be used every
 * time by exactly the people the beat exists for.
 *
 * What it does show is the time running out, because a screen that appears to
 * have stopped responding teaches a different lesson.
 */
export function HoldSilence({ ms, said }: {
  /** How long the silence lasts. From the org's pack, not from the engine. */
  ms: number;
  /** What the rep just said, kept on screen so the wait attaches to it. */
  said: string | null;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    // Frequent enough to look continuous, cheap enough not to matter.
    const tick = setInterval(() => setElapsed(Date.now() - started), 100);
    return () => clearInterval(tick);
  }, [ms]);

  const remaining = Math.max(0, ms - elapsed);
  const progress = Math.min(1, elapsed / ms);

  return (
    <main className="sim hold" aria-live="polite">
      <div className="hold-card">
        <h1>Wait.</h1>
        <p className="hold-lede">
          You have asked. Let them answer first — do not be the first to speak.
        </p>

        {said && <p className="hold-said">You said: “{said}”</p>}

        {/* A ring that empties rather than a number counting down: a countdown
            invites watching the number, and the thing being practised is
            sitting with the pause rather than timing it. */}
        <div className="hold-ring" aria-hidden="true">
          <div className="hold-ring-fill" style={{ transform: `scaleX(${1 - progress})` }} />
        </div>
        <p className="muted small">
          {remaining > 0 ? 'They are thinking about it.' : 'Here they come.'}
        </p>
      </div>
    </main>
  );
}
